const STORAGE_KEY = 'casticart.desktopNotifications.enabled.v1';
const NOTIFICATION_ICON = '/favicons/favicon_192.png';
const SERVICE_WORKER_URL = '/casticart-notification-sw.js';
const CHAT_NOTIFICATION_TYPE = 'chat.message';

const canUseWindow = () => typeof window !== 'undefined';

const safeLocalStorage = () => {
  if (!canUseWindow()) return null;
  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
};

const getNotificationApi = () => {
  if (!canUseWindow()) return null;
  return typeof window.Notification === 'function' ? window.Notification : null;
};

const getServiceWorkerApi = () => {
  if (!canUseWindow()) return null;
  return window.isSecureContext && 'serviceWorker' in window.navigator
    ? window.navigator.serviceWorker
    : null;
};

const readStoredPreference = () => {
  const storage = safeLocalStorage();
  if (!storage) return false;
  return storage.getItem(STORAGE_KEY) === 'true';
};

const writeStoredPreference = (enabled) => {
  const storage = safeLocalStorage();
  if (!storage) return;
  storage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
};

const isPageVisible = () => {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
};

const normalizeText = (value, fallback = '') =>
  String(value || fallback)
    .replace(/\s+/g, ' ')
    .trim();

const trimText = (value, maxLength) => {
  const text = normalizeText(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1))}…`;
};

export const isDesktopNotificationSupported = () => !!getNotificationApi();

export const getDesktopNotificationPermission = () => {
  const NotificationApi = getNotificationApi();
  if (!NotificationApi) return 'unsupported';
  return NotificationApi.permission || 'default';
};

export const isDesktopNotificationEnabled = () =>
  isDesktopNotificationSupported() &&
  getDesktopNotificationPermission() === 'granted' &&
  readStoredPreference();

export const disableDesktopNotifications = () => {
  writeStoredPreference(false);
  return {
    supported: isDesktopNotificationSupported(),
    permission: getDesktopNotificationPermission(),
    enabled: false,
  };
};

export const requestDesktopNotificationPermission = async () => {
  const NotificationApi = getNotificationApi();
  if (!NotificationApi) {
    writeStoredPreference(false);
    return { supported: false, permission: 'unsupported', enabled: false };
  }

  let permission = NotificationApi.permission || 'default';
  if (permission === 'default' && typeof NotificationApi.requestPermission === 'function') {
    permission = await NotificationApi.requestPermission();
  }

  const enabled = permission === 'granted';
  writeStoredPreference(enabled);
  return { supported: true, permission, enabled };
};

export const getDesktopNotificationState = () => ({
  supported: isDesktopNotificationSupported(),
  permission: getDesktopNotificationPermission(),
  enabled: isDesktopNotificationEnabled(),
});

const getNotificationRegistration = async () => {
  const serviceWorkerApi = getServiceWorkerApi();
  if (!serviceWorkerApi) return null;
  try {
    const existing = await serviceWorkerApi.getRegistration('/');
    if (existing && typeof existing.showNotification === 'function') {
      return existing;
    }
    const registered = await serviceWorkerApi.register(SERVICE_WORKER_URL, { scope: '/' });
    if (registered && typeof registered.showNotification === 'function') {
      return registered;
    }
    return serviceWorkerApi.ready;
  } catch {
    return null;
  }
};

const showDesktopNotification = async (title, options) => {
  const registration = await getNotificationRegistration();
  if (registration && typeof registration.showNotification === 'function') {
    await registration.showNotification(title, options);
    return { success: true, method: 'service-worker' };
  }

  const NotificationApi = getNotificationApi();
  if (!NotificationApi) return { success: false, reason: 'unsupported' };

  const notification = new NotificationApi(title, options);
  notification.onclick = () => {
    try {
      window.focus();
      const targetUrl = options?.data?.targetUrl || '';
      if (targetUrl) {
        window.location.assign(targetUrl);
      }
    } finally {
      notification.close();
    }
  };
  return { success: true, method: 'page' };
};

export const notifyDesktopFromPeerToast = async (rawToast = {}) => {
  if (rawToast.type !== CHAT_NOTIFICATION_TYPE) return false;
  if (!isDesktopNotificationEnabled()) return false;
  if (isPageVisible()) return false;

  const title = trimText(rawToast.title, 64) || 'チャットの新着';
  const body = trimText(rawToast.summary, 120);
  const targetUrl = normalizeText(rawToast.targetUrl);

  try {
    const result = await showDesktopNotification(title, {
      body,
      icon: NOTIFICATION_ICON,
      tag: rawToast.dedupeKey || rawToast.aggregateKey || targetUrl || title,
      renotify: false,
      data: { targetUrl },
    });
    return !!result.success;
  } catch {
    return false;
  }
};

export const sendDesktopNotificationTest = async () => {
  const NotificationApi = getNotificationApi();
  if (!NotificationApi) {
    return { success: false, reason: 'unsupported' };
  }
  if (getDesktopNotificationPermission() !== 'granted') {
    return { success: false, reason: 'permission' };
  }
  if (!readStoredPreference()) {
    return { success: false, reason: 'disabled' };
  }

  try {
    return await showDesktopNotification('Casticart デスクトップ通知', {
      body: '通知は有効です。新着チャットをPCの通知で知らせます。',
      icon: NOTIFICATION_ICON,
      tag: `casticart-desktop-notification-test-${Date.now()}`,
      requireInteraction: true,
      silent: false,
      data: { targetUrl: window.location.pathname || '/' },
    });
  } catch {
    return { success: false, reason: 'failed' };
  }
};
